import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useProcedureResult } from '@americanexpress/iguazu-rpc-hooks';
import { clearProcedureResult } from 'iguazu-rpc';
import { getAccountToken, getPublicGuid } from '../selectors';
import useQueryGetCredoPreferences from './useQueryGetCredoPreferences';
import { getMemberPreferenceByType } from '../utils';
export const makeCreateMemberPreferencesArgs = _ref => {
  let {
    publicGuid = '',
    accountToken = '',
    preferences = []
  } = _ref;
  const preferenceList = Array.isArray(preferences) ? preferences.map(preference => ({
    ...preference,
    accountToken
  })) : [];
  return {
    publicGuid,
    preferences: [...preferenceList]
  };
};
export const useUpdateMemberPreferences = (defer, preferences) => {
  const accountToken = useSelector(getAccountToken);
  const publicGuid = useSelector(getPublicGuid);
  const response = useProcedureResult({
    procedureName: 'createMemberPreferencesV1',
    args: makeCreateMemberPreferencesArgs({
      publicGuid,
      accountToken,
      preferences
    })
  }, {
    defer
  });
  return response;
};
const useDismissibleIguazu = function () {
  let {
    preferenceId
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const dispatch = useDispatch();
  const accountToken = useSelector(getAccountToken);
  const publicGuid = useSelector(getPublicGuid);

  // get the preferences from cache or API
  const {
    isLoading: isPreferencesLoading,
    data: credoPreferences = []
  } = useQueryGetCredoPreferences({
    types: [preferenceId],
    fallbackValue: true
  });
  const isAlreadyDismissed = getMemberPreferenceByType(credoPreferences, preferenceId);

  // set initial isLoading and isDismissed based on the existing preference, or lack thereof
  const [isLoading, setIsLoading] = useState(!isAlreadyDismissed);
  const [isDismissed, setIsDismissed] = useState(isAlreadyDismissed);
  const [isUpdating, setIsUpdating] = useState(false);

  // if preference was not in cache, set isDismissed when we get preference from the API
  useEffect(() => {
    setIsDismissed(isAlreadyDismissed);
  }, [isAlreadyDismissed, setIsDismissed]);

  // if preference was not in cache, toggle isLoading off when we get preference from the API
  useEffect(() => {
    if (isPreferencesLoading === false) setIsLoading(false);
  }, [setIsLoading, isLoading, isPreferencesLoading]);

  // setup to call CreateMemberPreferences with the preferenceId
  const {
    run: runUpdateMemberPreference
  } = useUpdateMemberPreferences(true, [{
    type: preferenceId,
    value: true
  }]);
  const runUpdateAsync = useCallback(async () => {
    await runUpdateMemberPreference();
    dispatch(clearProcedureResult({
      procedureName: 'readMemberPreferencesV1',
      args: {
        publicGuid,
        accountToken,
        timeout: 6000,
        types: [preferenceId]
      }
    }));
    setIsDismissed(true);
    setIsUpdating(false);
  }, [runUpdateMemberPreference, dispatch, setIsDismissed, setIsUpdating, accountToken, publicGuid, preferenceId]);
  const dismiss = useCallback(() => {
    setIsUpdating(true);
    runUpdateAsync();
  }, []);
  return useMemo(() => ({
    isDismissed,
    isLoading,
    dismiss,
    isUpdating
  }), [isLoading, isDismissed, dismiss, isUpdating]);
};
export default useDismissibleIguazu;